import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Radio, Users, CheckCircle, ListMusic, Plus, Share2, QrCode, 
  Copy, ChevronLeft, ChevronRight, Play, Heart, PoundSterling, 
  Smartphone, Globe, ArrowRight, Star, Sparkles, X, Volume2
} from 'lucide-react';
import { getMobileStyles } from '../styles/mobileDesignSystem';
import { useArtistLiveStatus } from '@/features/artist-mode/contexts/ArtistLiveStatusContext';

const MobileOnboarding = ({ isVisible, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationStep, setSimulationStep] = useState(0);
  const [isMarkingPlayed, setIsMarkingPlayed] = useState(false);
  const [simulationToggleOn, setSimulationToggleOn] = useState(false);
  
  // Use the actual isLive state from context instead of local state
  const { isLive } = useArtistLiveStatus();

  const welcomeText = "Welcome to Your Song Request!\nBefore we begin rocking and rolling, let's give you a quick backstage tour of how everything works!\n\nYou'll be receiving paid requests in no time!";

  // Helper function to get color classes
  const getColorClasses = (color) => {
    const colorMap = {
      green: {
        bg: 'bg-green-900/20',
        border: 'border-green-500/40',
        text: 'text-green-400'
      },
      purple: {
        bg: 'bg-purple-900/20',
        border: 'border-purple-500/40',
        text: 'text-purple-400'
      },
      red: {
        bg: 'bg-red-900/20',
        border: 'border-red-500/40',
        text: 'text-red-400'
      },
      blue: {
        bg: 'bg-blue-900/20',
        border: 'border-blue-500/40',
        text: 'text-blue-400'
      },
      cyan: {
        bg: 'bg-cyan-900/20',
        border: 'border-cyan-500/40',
        text: 'text-cyan-400'
      }
    };
    return colorMap[color] || colorMap.purple;
  };

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setShowWelcome(true);
        setIsTyping(true);
      }, 500);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isTyping && typedText.length < welcomeText.length) {
      const timeout = setTimeout(() => {
        setTypedText(welcomeText.slice(0, typedText.length + 1));
      }, 40); // 20% faster (was 50ms, now 40ms)
      return () => clearTimeout(timeout);
    } else if (isTyping && typedText.length >= welcomeText.length) {
      setIsTyping(false);
    }
  }, [isTyping, typedText, welcomeText]);

  const tourSteps = [
    {
      title: "Go Live",
      description: "Tap the 'Go Live' button to start receiving paid song requests from your audience!",
      icon: Radio,
      color: "green",
      simulation: "goLive"
    },
    {
      title: "QR Code",
      description: "Share your unique QR code and Profile URL with your audience. They scan it to access your song request page!",
      icon: QrCode,
      color: "purple",
      simulation: "qrSharing"
    },
    {
      title: "Audience View",
      description: "This is what your audience sees! They can browse your songs and make paid requests.",
      icon: Smartphone,
      color: "cyan",
      simulation: "audienceView"
    },
    {
      title: "Artist Request Page",
      description: "Watch paid requests roll in real-time. Each request shows the song, requester, and payment amount.",
      icon: Heart,
      color: "red",
      simulation: "requests"
    },
    {
      title: "Updates",
      description: "Watch your earnings grow in real-time as requests come in and you mark songs as played!",
      icon: PoundSterling,
      color: "green",
      simulation: "earnings"
    }
  ];

  const startTour = () => {
    setShowWelcome(false);
    setShowSimulation(true);
    setSimulationStep(0);
  };

  const nextStep = () => {
    if (simulationStep < tourSteps.length - 1) {
      setSimulationStep(simulationStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const prevStep = () => {
    if (simulationStep > 0) {
      setSimulationStep(simulationStep - 1);
    }
  };

  const completeOnboarding = () => {
    setShowSimulation(false);
    setCurrentStep(0);
    setIsMarkingPlayed(false);
    onComplete();
  };

  const handleToggleClick = () => {
    if (showSimulation && simulationStep === 0) {
      // For simulation, we'll use a local state just for the animation
      // This doesn't affect the real isLive state
      setSimulationToggleOn(true);
      // Wait for animation, then go to next step
      // NOTE: This is ONLY a visual simulation - it does NOT affect the real live status
      setTimeout(() => {
        nextStep();
        setSimulationToggleOn(false);
      }, 1500);
    } else if (!showSimulation) {
      // This is the real toggle - trigger the actual go live functionality
      // The parent component should handle this through props
      if (onComplete) {
        onComplete(); // This will close the onboarding and allow the real toggle to work
      }
    }
  };

  const handleMarkAsPlayed = () => {
    if (simulationStep === 3) { // Step 4 (0-indexed)
      setIsMarkingPlayed(true);
      // Wait for animation, then go to next step
      setTimeout(() => {
        nextStep();
        setIsMarkingPlayed(false);
      }, 1500);
    }
  };

  const renderSimulation = () => {
    const step = tourSteps[simulationStep];
    
    switch (step.simulation) {
             case "goLive":
         return (
           <div className="space-y-4">
             {/* Floating Toggle */}
             <motion.div 
               className="flex justify-center items-center mb-6"
               initial={{ opacity: 0, scale: 0.8 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <motion.button
                 onClick={handleToggleClick}
                 className={`relative w-20 h-10 rounded-full p-1 transition-colors duration-300 ${
                   (showSimulation ? simulationToggleOn : isLive) ? 'bg-green-600' : 'bg-gray-600'
                 }`}
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <motion.div
                   className={`w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center ${
                     (showSimulation ? simulationToggleOn : isLive) ? 'ml-10' : 'ml-0'
                   }`}
                   transition={{ 
                     type: "spring", 
                     stiffness: 500, 
                     damping: 30 
                   }}
                 >
                   <Radio className={`w-4 h-4 ${(showSimulation ? simulationToggleOn : isLive) ? 'text-green-600' : 'text-gray-400'}`} />
                 </motion.div>
               </motion.button>
             </motion.div>

             {/* Status Display */}
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-6 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="text-center space-y-4">
                 <div className="w-16 h-16 bg-purple-900/20 border-2 border-purple-500/60 rounded-full flex items-center justify-center mx-auto">
                   <Radio className="w-8 h-8 text-purple-400" />
                 </div>
                 <h3 className="text-xl font-bold text-white">
                   {(showSimulation ? simulationToggleOn : isLive) ? "You're Now Live!" : "You're Currently Offline"}
                 </h3>
                 <p className="text-gray-400">
                   {(showSimulation ? simulationToggleOn : isLive) ? "Ready to receive paid requests!" : "Switch the Go LIVE toggle ON to start receiving paid requests"}
                 </p>
               </div>
             </motion.div>
             
             {(showSimulation ? simulationToggleOn : isLive) && (
               <motion.div 
                 className="bg-green-900/20 border border-green-500/40 rounded-xl p-4"
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.5 }}
               >
                 <div className="flex items-center justify-center space-x-2">
                   <Radio className="w-4 h-4 text-green-400" />
                   <span className="text-green-400 font-semibold">LIVE</span>
                   <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                 </div>
               </motion.div>
             )}
           </div>
         );

      case "dashboard":
        return (
          <div className="space-y-4">
            <motion.div 
              className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-900/20 border border-purple-500/40 rounded-lg p-3 text-center">
                  <Music className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <div className="text-lg font-bold text-white">3</div>
                  <div className="text-xs text-gray-400">Pending</div>
                </div>
                <div className="bg-green-900/20 border border-green-500/40 rounded-lg p-3 text-center">
                  <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className="text-lg font-bold text-white">12</div>
                  <div className="text-xs text-gray-400">Played</div>
                </div>
                <div className="bg-blue-900/20 border border-blue-500/40 rounded-lg p-3 text-center">
                  <ListMusic className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-lg font-bold text-white">5</div>
                  <div className="text-xs text-gray-400">Setlists</div>
                </div>
                <div className="bg-cyan-900/20 border border-cyan-500/40 rounded-lg p-3 text-center">
                  <Users className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <div className="text-lg font-bold text-white">£45</div>
                  <div className="text-xs text-gray-400">Earnings</div>
                </div>
              </div>
            </motion.div>
          </div>
        );

       case "requests":
         return (
           <div className="space-y-4">
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="space-y-3">
                 <motion.div 
                   className="bg-red-900/20 border border-red-500/40 rounded-lg p-3"
                   initial={{ opacity: 0, x: -50 }}
                   animate={{ opacity: 1, x: 0 }}
                   transition={{ delay: 0.3 }}
                 >
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold">Wonderwall</div>
                       <div className="text-gray-400 text-sm">by Oasis</div>
                       <div className="flex items-center space-x-2 mt-1">
                         <Heart className="w-3 h-3 text-red-400" />
                         <span className="text-xs text-gray-400">Requested by Sarah</span>
                       </div>
                     </div>
                     <div className="text-right">
                       <div className="text-green-400 font-bold">£5.00</div>
                       <div className="text-xs text-gray-400">2 min ago</div>
                     </div>
                   </div>
                 </motion.div>
                 
                                   <motion.div 
                    className="bg-red-900/20 border border-red-500/40 rounded-lg p-3"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-semibold">Sweet Caroline</div>
                        <div className="text-gray-400 text-sm">by Neil Diamond</div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Heart className="w-3 h-3 text-red-400" />
                          <span className="text-xs text-gray-400">Requested by Mike</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-bold">£3.50</div>
                        <div className="text-xs text-gray-400">5 min ago</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="bg-red-900/20 border border-red-500/40 rounded-lg p-3"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-semibold">Everything I Do</div>
                        <div className="text-gray-400 text-sm">by Bryan Adams</div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Heart className="w-3 h-3 text-red-400" />
                          <span className="text-xs text-gray-400">Requested by Emma</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-bold">£4.00</div>
                        <div className="text-xs text-gray-400">Just now</div>
                      </div>
                    </div>
                  </motion.div>
               </div>
             </motion.div>

             <motion.div 
               className="bg-green-900/20 border border-green-500/40 rounded-lg p-3"
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 1.2 }}
             >
               <div className="flex items-center justify-center space-x-2">
                 <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                 <span className="text-green-400 font-semibold">New request received!</span>
               </div>
             </motion.div>
           </div>
         );

             case "markPlayed":
         return (
           <div className="space-y-4">
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="space-y-3">
                 <motion.div 
                   className="bg-red-900/20 border border-red-500/40 rounded-lg p-3"
                   initial={{ opacity: 0, x: -20 }}
                   animate={{ opacity: 1, x: 0 }}
                   transition={{ delay: 0.3 }}
                 >
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold">Wonderwall</div>
                       <div className="text-gray-400 text-sm">by Oasis</div>
                       <div className="flex items-center space-x-2 mt-1">
                         <Heart className="w-3 h-3 text-red-400" />
                         <span className="text-xs text-gray-400">Requested by Sarah</span>
                       </div>
                     </div>
                     <motion.button
                       className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.95 }}
                       onClick={handleMarkAsPlayed}
                     >
                       Mark as Played
                     </motion.button>
                   </div>
                 </motion.div>
               </div>
             </motion.div>
             
             {simulationStep === 3 && (
               <>
                 <motion.div 
                   className="bg-green-900/20 border border-green-500/40 rounded-xl p-3"
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.5, delay: 0.8 }}
                 >
                   <div className="flex items-center justify-between">
                     <div className="flex items-center space-x-2">
                       <CheckCircle className="w-4 h-4 text-green-400" />
                       <span className="text-green-400 font-semibold">Wonderwall - Played</span>
                     </div>
                     <div className="text-xs text-gray-400">2 min ago</div>
                   </div>
                 </motion.div>

                 <motion.div 
                   className="bg-blue-900/20 border border-blue-500/40 rounded-lg p-3"
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.5, delay: 1.1 }}
                 >
                   <div className="flex items-center justify-center space-x-2">
                     <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                     <span className="text-blue-400 font-semibold">Stats updated!</span>
                   </div>
                 </motion.div>
               </>
             )}
           </div>
         );

             case "setlists":
         return (
           <div className="space-y-3">
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-3 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="grid grid-cols-1 gap-2">
                 <div className="bg-blue-900/20 border border-blue-500/40 rounded-lg p-2">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold text-sm">All Songs</div>
                     </div>
                     <div className="text-blue-400 text-xs">Active</div>
                   </div>
                 </div>
                 
                 <div className="bg-gray-800/20 border border-gray-600/40 rounded-lg p-2">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold text-sm">Pub Classics</div>
                     </div>
                     <div className="text-gray-400 text-xs">Select</div>
                   </div>
                 </div>

                 <div className="bg-gray-800/20 border border-gray-600/40 rounded-lg p-2">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold text-sm">Current Pop Songs</div>
                     </div>
                     <div className="text-gray-400 text-xs">Select</div>
                   </div>
                 </div>

                 <div className="bg-gray-800/20 border border-gray-600/40 rounded-lg p-2">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="text-white font-semibold text-sm">Crowd Pleasers</div>
                     </div>
                     <div className="text-gray-400 text-xs">Select</div>
                   </div>
                 </div>
               </div>
             </motion.div>
           </div>
         );

      case "audienceView":
        return (
          <div className="space-y-4">
            <motion.div 
              className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                  <Music className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">John's Live Performance</h3>
                <div className="bg-green-900/20 border border-green-500/40 rounded-lg p-2">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-semibold">LIVE NOW</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="space-y-3">
                <div className="bg-purple-900/20 border border-purple-500/40 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-white font-semibold">Wonderwall</div>
                      <div className="text-gray-400 text-sm">by Oasis</div>
                    </div>
                    <div className="text-right">
                      <div className="text-purple-400 font-bold">£5.00</div>
                      <motion.button
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium mt-1"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Request
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        );

             case "qrSharing":
         return (
           <div className="space-y-4">
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-6 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="text-center space-y-4">
                 <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center mx-auto p-2">
                   <QrCode className="w-16 h-16 text-black" />
                 </div>
                 <h3 className="text-xl font-bold text-white">Your QR Code</h3>
                 <p className="text-gray-400">Share this with your audience</p>
                 <motion.button
                   className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-medium flex items-center space-x-2 mx-auto"
                   whileHover={{ scale: 1.05 }}
                   whileTap={{ scale: 0.95 }}
                 >
                   <Share2 className="w-5 h-5" />
                   <span>Share QR Code</span>
                 </motion.button>
               </div>
             </motion.div>
             
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5, delay: 0.3 }}
             >
               <div className="text-center space-y-2">
                 <Smartphone className="w-8 h-8 text-purple-400 mx-auto" />
                 <p className="text-purple-400 font-semibold">Audience scans QR code</p>
                 <p className="text-gray-400 text-sm">They access your song request page</p>
               </div>
             </motion.div>
           </div>
         );

       case "earnings":
         return (
           <div className="space-y-4">
             <motion.div 
               className="bg-gray-900/50 rounded-xl p-4 border border-gray-700"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.5 }}
             >
               <div className="space-y-4">
                 <div className="text-center">
                   <h3 className="text-xl font-bold text-white mb-2">Live Earnings</h3>
                   <div className="text-3xl font-bold text-green-400">£45.50</div>
                 </div>
                 
                 <div className="space-y-2">
                   <motion.div 
                     className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/40 rounded-lg"
                     initial={{ opacity: 0, x: -20 }}
                     animate={{ opacity: 1, x: 0 }}
                     transition={{ delay: 0.5 }}
                   >
                     <div className="flex items-center space-x-3">
                       <Heart className="w-4 h-4 text-red-400" />
                       <div>
                         <div className="text-white font-medium">Wonderwall</div>
                         <div className="text-xs text-gray-400">+£5.00</div>
                       </div>
                     </div>
                     <div className="text-green-400 font-bold">£50.50</div>
                   </motion.div>
                   
                   <motion.div 
                     className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/40 rounded-lg"
                     initial={{ opacity: 0, x: -20 }}
                     animate={{ opacity: 1, x: 0 }}
                     transition={{ delay: 0.8 }}
                   >
                     <div className="flex items-center space-x-3">
                       <CheckCircle className="w-4 h-4 text-green-400" />
                       <div>
                         <div className="text-white font-medium">Song Played</div>
                         <div className="text-xs text-gray-400">Earnings updated</div>
                       </div>
                     </div>
                     <div className="text-green-400 font-bold">£50.50</div>
                   </motion.div>
                 </div>
               </div>
             </motion.div>
           </div>
         );

       default:
         return null;
     }
   };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {showWelcome && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-gray-900/95 border border-purple-500/40 rounded-2xl p-6 max-w-sm w-full"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="text-center space-y-6">
              <motion.div
                className="w-20 h-20 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 15 }}
              >
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>
              
              <div className="space-y-4">
                <div className="text-white font-semibold text-lg leading-relaxed">
                  {typedText}
                  {isTyping && <span className="animate-pulse">|</span>}
                </div>
                
                {!isTyping && (
                  <motion.button
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center space-x-2 mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={startTour}
                  >
                    <span>Start Tour</span>
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {showSimulation && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-gray-900/95 border border-purple-500/40 rounded-2xl p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {(() => {
                    const colors = getColorClasses(tourSteps[simulationStep].color);
                    const IconComponent = tourSteps[simulationStep].icon;
                    return (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg} ${colors.border}`}>
                        <IconComponent className={`w-5 h-5 ${colors.text}`} />
                      </div>
                    );
                  })()}
                  <div>
                    <h3 className="text-white font-bold text-lg">{tourSteps[simulationStep].title}</h3>
                    <p className="text-gray-400 text-sm">{simulationStep + 1} of {tourSteps.length}</p>
                  </div>
                </div>
                <button
                  onClick={completeOnboarding}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Description */}
              <p className="text-gray-300 leading-relaxed">
                {tourSteps[simulationStep].description}
              </p>

              {/* Simulation */}
              {renderSimulation()}

              {/* Navigation */}
              <div className="flex items-center justify-between pt-4">
                <motion.button
                  className={`px-4 py-2 rounded-lg font-medium ${
                    simulationStep === 0 
                      ? 'text-gray-500 cursor-not-allowed' 
                      : 'text-purple-400 hover:text-purple-300'
                  }`}
                  onClick={prevStep}
                  disabled={simulationStep === 0}
                  whileHover={simulationStep > 0 ? { scale: 1.05 } : {}}
                  whileTap={simulationStep > 0 ? { scale: 0.95 } : {}}
                >
                  <ChevronLeft className="w-5 h-5" />
                </motion.button>

                <div className="flex space-x-2">
                  {tourSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === simulationStep ? 'bg-purple-400' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                {simulationStep === 0 && simulationToggleOn ? (
                  <motion.div
                    className="text-green-400 font-medium flex items-center space-x-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Going Live...</span>
                  </motion.div>
                ) : simulationStep === 3 && isMarkingPlayed ? (
                  <motion.div
                    className="text-green-400 font-medium flex items-center space-x-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Marking as Played...</span>
                  </motion.div>
                ) : (
                  <motion.button
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-2 rounded-lg font-medium flex items-center space-x-2"
                    onClick={nextStep}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>{simulationStep === tourSteps.length - 1 ? 'Finish' : 'Next'}</span>
                    <ChevronRight className="w-5 h-5" />
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileOnboarding;
